import { z } from 'zod';

import { TranslatedTextSchema } from 'src/types/common';

export enum LessonType {
  STANDARD = 'STANDARD',
  EXAM = 'EXAM',
  TEMPLATE = 'TEMPLATE',
  TEMPLATE_EXAM = 'TEMPLATE_EXAM',
  PRESENCE = 'PRESENCE',
  TEMPLATE_PRESENCE = 'TEMPLATE_PRESENCE',
}

export const LessonTypeSchema = z.enum(LessonType);

export const QuestionTypeSchema = z.enum(['SINGLE_CHOICE', 'MULTIPLE_CHOICE']);

export const ExaminationAnswerSchema = z.object({
  id: z.number(),
  text: TranslatedTextSchema,
  orderIndex: z.number(),
  isCorrect: z.boolean().optional(),
});

export const ExaminationQuestionSchema = z.object({
  id: z.number(),
  text: TranslatedTextSchema,
  questionType: QuestionTypeSchema,
  weight: z.number().optional(),
  orderIndex: z.number(),
  imageUrl: z.string().nullable().optional(),
  answerOptions: z.array(ExaminationAnswerSchema).nullable(),
});

export const LessonSchema = z.object({
  id: z.number(),
  name: z.string().nullable().optional(),
  accessGroupIds: z.unknown().nullable().optional(),
  description: z.string().nullable().optional(),
  localeText: z.unknown().nullable().optional(),
  unusedLocaleCertificate: z.unknown().nullable().optional(),
  archived: z.boolean().nullable().optional(),
  wpPostId: z.number().nullable().optional(),
  educationalAmount: z.number().nullable().optional(),
  version: z.string().nullable().optional(),
  state: z.string().nullable().optional(),
  subscriptionData: z.unknown().nullable().optional(),
  cost: z.number().nullable().optional(),
  level: z.string().nullable().optional(),
  ownerGroupIds: z.unknown().nullable().optional(),
  audienceTypes: z.unknown().nullable().optional(),
  type: z.string().optional(),
  categoryIds: z.unknown().nullable().optional(),
  createdDate: z.string().nullable().optional(),
  lastModifiedDate: z.string().nullable().optional(),
  author: z.string().nullable().optional(),
  createdByAdminRole: z.string().nullable().optional(),
  createdBy: z.number().nullable().optional(),
  isSmedexLesson: z.boolean().optional(),
});

export const ExaminationSchema = z.object({
  id: z.number(),
  name: TranslatedTextSchema,
  description: TranslatedTextSchema.nullable().optional(),
  timeLimitMinutes: z.number().nullable().optional(),
  requiresAccessCode: z.boolean().optional(),
  randomOrder: z.boolean().optional(),
  questionsPerExam: z.number().nullable().optional(),
  maxAttempts: z.number().nullable().optional(),
  passPercentage: z.number().optional(),
  renewalPeriodDays: z.number().nullable().optional(),
  state: z.string().optional(),
  archived: z.boolean().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
  lesson: LessonSchema.optional(),
  questions: z.array(ExaminationQuestionSchema).optional(),
  remainingAttempts: z.number().nullable().optional(),
  nextAttemptAllowedAt: z.string().nullable().optional(),
});

export const ExaminationAttemptSchema = z.object({
  id: z.number(),
  examinationId: z.number(),
  userId: z.number(),
  startedDate: z.coerce.date().nullable(),
  completedDate: z.coerce.date().nullable(),
  score: z.number().nullable(),
  passed: z.boolean(),
  renewalDueDate: z.coerce.date().nullable(),
  timeLimitMinutes: z.number().nullable().optional(),
});

export type QuestionType = z.infer<typeof QuestionTypeSchema>;
export type Lesson = z.infer<typeof LessonSchema>;
export type Examination = z.infer<typeof ExaminationSchema> & {
  lessonType?: LessonType;
};
export type ExaminationAttempt = z.infer<typeof ExaminationAttemptSchema>;
export type ExaminationAnswer = z.infer<typeof ExaminationAnswerSchema>;
export type ExaminationQuestion = z.infer<typeof ExaminationQuestionSchema>;
